import React, { useState, useMemo } from 'react';
import { View, FlatList, StyleSheet, TouchableOpacity, ActivityIndicator, Alert } from 'react-native';
import { Text, TextInput, Button, Card, Searchbar } from 'react-native-paper';
import Icon from 'react-native-vector-icons/FontAwesome';
import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store';
import {
  useGetAmCategoriesQuery,
  useInsertAmCategoryMutation,
  useDeleteAmCategoryMutation,
  type AmCategory
} from '../redux/services/amcategoryApi';
import { ShowToast } from '../Common/common';

import T from '../Components/T';
import { tp } from '../utils/translateText';
/** Asset categories: list, add/edit, delete, and seed the standard hotel set. */
const AmCategoryList = ({ navigation }: any) => {
  const company = useSelector((state: RootState) => state.gdata?.company);
  const { data: categories = [], isLoading } = useGetAmCategoriesQuery(company || '', { skip: !company });
  const [saveCategory, { isLoading: isSaving }] = useInsertAmCategoryMutation();
  const [deleteCategory] = useDeleteAmCategoryMutation();

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Partial<AmCategory> | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? categories.filter(c => c.ct_name.toLowerCase().includes(q)) : categories;
  }, [categories, search]);

  const save = async () => {
    if (!editing?.ct_name?.trim()) {
      ShowToast('Category name is required', 'error');
      return;
    }
    try {
      // The API saves through one POST; ct_id > 0 updates
      await saveCategory({
        ct_id: editing.ct_id || 0,
        ct_name: editing.ct_name.trim(),
        ct_depmethod: editing.ct_depmethod || 'S',
        ct_deprate: editing.ct_deprate
      } as any).unwrap();
      ShowToast('Category saved');
      setEditing(null);
    } catch (e: any) {
      ShowToast(e?.data?.message || 'Could not save category', 'error');
    }
  };

  const confirmDelete = (c: AmCategory) =>
    Alert.alert(tp("Delete category"), `Delete "${c.ct_name}"?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          try {
            await deleteCategory(c.ct_id).unwrap();
          } catch (e: any) {
            ShowToast(e?.data?.message || 'Could not delete (category may be in use)', 'error');
          }
        }
      }
    ]);

  return (
    <View style={styles.container}>
      <Searchbar placeholder={tp("Search categories")} value={search} onChangeText={setSearch} style={styles.search} />
      <View style={styles.actions}>
        <TouchableOpacity style={styles.pill} onPress={() => setEditing({ ct_depmethod: 'S' })}>
          <Icon name="plus" size={12} color="#fff" />
          <Text style={styles.pillText}> <T>New category</T></Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.pill, styles.pillAlt]} onPress={() => navigation.navigate('AmSeedCatalogue')}>
          <Icon name="magic" size={12} color="#2E7D32" />
          <Text style={[styles.pillText, { color: '#2E7D32' }]}> <T>Seed from template</T></Text>
        </TouchableOpacity>
      </View>

      {editing && (
        <Card style={styles.editor}>
          <TextInput
            label="Category name *"
            value={editing.ct_name || ''}
            onChangeText={v => setEditing({ ...editing, ct_name: v })}
            mode="outlined"
            style={styles.input}
          />
          <TextInput
            label="Depreciation rate % (optional)"
            value={editing.ct_deprate != null ? String(editing.ct_deprate) : ''}
            onChangeText={v => setEditing({ ...editing, ct_deprate: v ? parseFloat(v) || 0 : undefined })}
            keyboardType="decimal-pad"
            mode="outlined"
            style={styles.input}
          />
          <View style={styles.editorBtns}>
            <Button mode="contained" onPress={save} loading={isSaving} disabled={isSaving}>
              Save
            </Button>
            <Button mode="outlined" onPress={() => setEditing(null)} disabled={isSaving}>
              Cancel
            </Button>
          </View>
        </Card>
      )}

      {isLoading ? (
        <ActivityIndicator size="large" color="#2E7D32" style={{ flex: 1 }} />
      ) : filtered.length === 0 ? (
        <View style={styles.empty}>
          <Icon name="list" size={50} color="#ccc" />
          <Text style={styles.emptyText}><T>No categories yet - tap "Seed from template"</T></Text>
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={c => String(c.ct_id)}
          contentContainerStyle={{ padding: 10 }}
          renderItem={({ item }) => (
            <View style={styles.item}>
              <TouchableOpacity style={{ flex: 1 }} onPress={() => setEditing(item)}>
                <Text style={styles.title}>{item.ct_name}</Text>
                <Text style={styles.sub}>
                  {item.ct_depmethod === 'W' ? 'WDV' : 'Straight line'}
                  {item.ct_deprate ? `  ·  ${item.ct_deprate}%` : ''}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => confirmDelete(item)} style={styles.del}>
                <Icon name="trash" size={18} color="#d32f2f" />
              </TouchableOpacity>
            </View>
          )}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  search: { margin: 10, marginBottom: 6 },
  actions: { flexDirection: 'row', gap: 8, marginHorizontal: 10, marginBottom: 6 },
  pill: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#2E7D32', borderRadius: 14, paddingHorizontal: 12, paddingVertical: 6 },
  pillAlt: { backgroundColor: '#fff', borderWidth: 1, borderColor: '#2E7D32' },
  pillText: { color: '#fff', fontSize: 12, fontWeight: 'bold' },
  editor: { margin: 10, padding: 12 },
  input: { marginBottom: 8 },
  editorBtns: { flexDirection: 'row', gap: 10 },
  item: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 8, padding: 12, marginBottom: 8, borderLeftWidth: 4, borderLeftColor: '#2E7D32' },
  title: { fontSize: 14, fontWeight: 'bold' },
  sub: { fontSize: 12, color: '#666', marginTop: 2 },
  del: { padding: 8 },
  empty: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
  emptyText: { marginTop: 10, fontSize: 14, color: '#999', textAlign: 'center' }
});

export default AmCategoryList;
